import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { appliquerTaille, lireTaille, PALIERS_TAILLE } from '../etat/tailleTexte'

/**
 * Cadre commun à tous les écrans : le bandeau de titre, l'heure et le témoin de
 * liaison. Chaque écran le rend lui-même, ce qui lui laisse la main sur ce qui
 * s'affiche à droite du titre (onglets de caisse, filtres, actions).
 */
export function Ecran({
  titre,
  sousTitre,
  outils,
  children,
}: {
  titre: string
  sousTitre: string
  outils?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="principal">
      <header className="entete">
        <div className="entete-titre">
          <h2>{titre}</h2>
          <div className="etiquette">{sousTitre}</div>
        </div>
        {outils}
        <div className="entete-fin">
          <ReglageTailleTexte />
          <Horloge />
          <TemoinLiaison />
        </div>
      </header>
      <div className="zone">{children}</div>
    </div>
  )
}

/**
 * A- / A+ pour agrandir ou réduire tout le site, gardé d'une visite à l'autre.
 * Utile à qui lit moins bien de près — l'iPad de comptoir n'a pas de réglage
 * d'accessibilité à sa portée en plein service.
 */
function ReglageTailleTexte() {
  const [taille, setTaille] = useState(lireTaille)

  const changer = (sens: 1 | -1) => {
    const rang = PALIERS_TAILLE.indexOf(taille as (typeof PALIERS_TAILLE)[number])
    const suivant = PALIERS_TAILLE[Math.min(Math.max(rang + sens, 0), PALIERS_TAILLE.length - 1)]!
    setTaille(appliquerTaille(suivant))
  }

  return (
    <div className="reglage-taille-texte">
      <button
        type="button"
        className="btn btn-secondaire btn-icone"
        aria-label="Réduire la taille du texte"
        disabled={taille <= PALIERS_TAILLE[0]!}
        onClick={() => changer(-1)}
      >
        a
      </button>
      <button
        type="button"
        className="btn btn-secondaire btn-icone"
        aria-label="Agrandir la taille du texte"
        disabled={taille >= PALIERS_TAILLE[PALIERS_TAILLE.length - 1]!}
        onClick={() => changer(1)}
      >
        A
      </button>
    </div>
  )
}

const FORMAT_DATE = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
})
const FORMAT_HEURE = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' })

export function Horloge() {
  const [maintenant, setMaintenant] = useState(() => new Date())

  useEffect(() => {
    // Recalage sur la minute pleine, puis toutes les minutes.
    let intervalle: number | undefined
    const debut = window.setTimeout(
      () => {
        setMaintenant(new Date())
        intervalle = window.setInterval(() => setMaintenant(new Date()), 60_000)
      },
      (60 - new Date().getSeconds()) * 1000,
    )
    return () => {
      window.clearTimeout(debut)
      if (intervalle) window.clearInterval(intervalle)
    }
  }, [])

  const date = FORMAT_DATE.format(maintenant)
  return (
    <div className="horloge">
      <span className="horloge-date">{date.charAt(0).toUpperCase() + date.slice(1)}</span>
      <br />
      <span className="horloge-heure">{FORMAT_HEURE.format(maintenant)}</span>
    </div>
  )
}

function TemoinLiaison() {
  const [enLigne, setEnLigne] = useState(() => navigator.onLine)

  useEffect(() => {
    const majEnLigne = () => setEnLigne(true)
    const majHorsLigne = () => setEnLigne(false)
    window.addEventListener('online', majEnLigne)
    window.addEventListener('offline', majHorsLigne)
    return () => {
      window.removeEventListener('online', majEnLigne)
      window.removeEventListener('offline', majHorsLigne)
    }
  }, [])

  return (
    <div className={enLigne ? 'temoin' : 'temoin hors-ligne'}>
      <span className="diode" />
      {enLigne ? 'Caisse connectée' : 'Hors ligne'}
    </div>
  )
}
